import React, { useState, Fragment, useEffect } from "react";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useQueryString } from "@/hooks";
import { usePartnerQuery } from "../../api";
import { useAuthV2 } from "@/offering/auth_portal_custom_login_provider";
import { colors } from "@/offering_v2/components/colors";
import { Spacer } from "@/offering/spacer";
import { Text } from "@/offering/text";
// @ts-ignore
import passwordPolicy from "password-sheriff";
// @ts-ignore
import { format } from "password-sheriff/lib/helper";
import { Icon } from "@/components";

const pwdPolicy = passwordPolicy("good");

export function PortalSignupPage() {
  const query = useQueryString();
  const partnerSlug = query.partnerSlug || localStorage.getItem("partnerSlug");
  const auth0 = useAuthV2();
  const partner = usePartnerQuery(partnerSlug);
  const [email, setEmail] = useState(query.email);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const explainedPwdPolicy = pwdPolicy.explain();
  const missingReqs = password ? pwdPolicy.missing(password) : { rules: [] };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!pwdPolicy.check(password)) {
      setError("Please fix the password requirements");
      return;
    }

    setIsLoading(true);

    try {
      await auth0.signup(email, password);
    } catch (err) {
      setError("An error occurred during signup");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (partnerSlug) {
      localStorage.setItem("partnerSlug", partnerSlug);
    }
  }, [partnerSlug]);

  if (!partner) {
    return null;
  }

  return (
    <Container className="mt-5">
      <div className="d-flex flex-column align-items-center">
        {partner.logoUrl && (
          <img
            src={partner.logoUrl}
            alt={partner.name}
            style={{ maxWidth: "120px", marginBottom: "0.5rem" }}
          />
        )}
        <div className="col-12 col-md-6 col-lg-4">
          <h2 className="text-center mb-4">Sign up for {partner.name}</h2>
          {error && (
            <Alert className="mb-2" variant="danger">
              {error}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={!!query.email}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
              />
              <Spacer size={16} />
              <Text weight="bold" size="xs">
                Password must
              </Text>
              <Spacer size={8} />
              {explainedPwdPolicy.map((policy: any, index: number) => {
                const isVerified = missingReqs.rules[index]?.verified;
                const textColor = isVerified
                  ? "success"
                  : error
                  ? "error"
                  : "default";

                const iconClass = isVerified
                  ? "text-color-success"
                  : error
                  ? "text-color-error"
                  : "text-color-default";

                return (
                  <Fragment key={policy.message}>
                    <div className="d-flex align-items-center">
                      <Icon
                        className={iconClass}
                        name={isVerified ? "check" : "x"}
                        size={16}
                        color={textColor}
                      />
                      <Spacer direction="horizontal" size={4} />
                      <Text size="xs" color={textColor}>
                        {format(policy.message, ...policy.format)}
                      </Text>
                    </div>
                    <Spacer size={4} />
                    {policy.items?.map?.((item: any, itemIndex: number) => {
                      const isItemVerified =
                        missingReqs.rules[index]?.items[itemIndex]?.verified;
                      const policyTextColor = isItemVerified
                        ? "success"
                        : error
                        ? "error"
                        : "default";

                      return (
                        <Fragment key={item.message}>
                          <Text
                            className="ps-4"
                            size="xs"
                            color={policyTextColor}
                          >
                            {"     "}• {item.message}
                          </Text>
                          <Spacer size={4} />
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !pwdPolicy.check(password)}
              className="w-100 d-flex align-items-center justify-content-center gap-2 mt-4"
            >
              {isLoading && <Spinner animation="border" size="sm" />}
              {isLoading ? "Creating account..." : "Sign Up"}
            </Button>

            <div className="text-center mt-3">
              <a
                style={{ color: colors.eggPlantCore }}
                href={`/portal/auth/login?partnerSlug=${partnerSlug}${
                  query.email ? `&email=${query.email}` : ""
                }`}
              >
                Already have an account? Log in
              </a>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
}
